import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "../../Assets/Styles/pages/homeRedesign.scss";
import "../../Assets/Styles/pages/batchWatermarkRedesign.scss";

import AppSidebar from "../../Components/Layout/AppSidebar";
import AppTopbar from "../../Components/Layout/AppTopbar";
import SettingsModal from "../../Components/SettingsModal/settingsModal";
import SignIn from "../../Components/SignIn/signIn";
import SelectTemplate from "../../Components/SelectTemplate/selectTemplate";
import ImageSourceSelectorModal, {
  BATCH_LIMIT_FREE,
} from "../../Components/Modals/ImageSourceSelectorModal";
import PremiumGateDialog from "../../Components/Common/PremiumGateDialog";
import { usePremiumStatus } from "../../hooks/usePremiumStatus";
import { GetSettings } from "../../actions/Settings/Setting";
import { GetAllTemplates } from "../../actions/Template/Template";
import { setBatchImages, setID } from "../../Redux/UserSlice/userSlice";
import { deleteAll } from "../../Redux/WaterMarks/watermarks";
import { skipBatchUrlImage } from "../../Redux/MainImage/imageSlice";
import { auth } from "../../Config/firebase-config";
import type { RootState } from "../../Redux/store";
import usePageMetadata from "../../Utils/usePageMetadata";

interface TemplateItem {
  id: string;
  name: string;
  date?: number;
  media?: { thumbUrl?: string; originalUrl?: string; url?: string; largeUrl?: string } | null;
  thumbUrl?: string;
  thumbnailUrl?: string;
  originalUrl?: string;
  url?: string;
}

/**
 * Dedicated "Batch Watermark" page -- ported from the Batch2 reference build, which treats
 * batch watermarking as its own full page/flow rather than a stack of modals:
 *   1. Empty-state upload prompt (page content, not a modal) -> real image picker.
 *   2. Uploaded photos reviewed in an on-page grid (select/remove/add more).
 *   3. "Apply Watermark" opens the template picker (a real modal, same as the reference's
 *      own TemplatePicker overlay).
 *   4. Confirming a template navigates into the existing canvas editor (/edit) exactly like
 *      the old flow did, just with upload and template-pick order reversed.
 */
const BatchWatermark: React.FC = () => {
  usePageMetadata("Batch Watermark - eZy WaterMark");
  const dispatch = useDispatch();
  const history = useHistory();
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  const mainImageState = useSelector((state: RootState) => state.mainImage);
  const isLoggedIn = Boolean(Object.keys(userSliceState.user_Auth ?? {}).length);
  const photos = mainImageState.batchurls;

  // Same displayName-resolution pattern as AddTemplate.tsx/ProfilePage.tsx.
  const firebaseUser = auth.currentUser;
  const displayName =
    firebaseUser?.displayName ||
    (userSliceState.user_Auth?.displayName as string) ||
    "there";

  const [openSettings, setOpenSettings] = useState(false);
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  // Forces a fresh ImageSourceSelectorModal instance on every "Choose Photos"/"Add More
  // Photos" click -- without it, if the user cancels the native file dialog (no onChange
  // fires), the modal's `open` prop never toggles false->true again, so its auto-click
  // effect wouldn't refire on a second click.
  const [uploadNonce, setUploadNonce] = useState(0);
  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [portraitTemp, setPortrait] = useState<TemplateItem | null>(null);
  const [landcsapeTemp, setLandcsape] = useState<TemplateItem | null>(null);
  const [multiple, setMultiple] = useState(true);
  const [showBatchLimitDialog, setShowBatchLimitDialog] = useState(false);
  const isPremium = usePremiumStatus();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<FileList | null>(null);
  // True while ImageSourceSelectorModal is decoding/converting the picked or dropped files.
  // Navigating away (Apply Watermark) mid-way abandons that loop with only part of the batch
  // added, which then resurfaces as a stale, partially-populated batch on the next visit.
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) history.push("/home");
  }, [isLoggedIn, history]);

  useEffect(() => {
    void Promise.all([dispatch(GetAllTemplates()), dispatch(GetSettings())]);
  }, [dispatch]);

  // Fresh visit to the page should always start from an empty batch, same as /home did before.
  // Also clears any template id left over from a previous session -- photos are uploaded here
  // *before* a template is picked, so a stale current_TemplateID would otherwise get baked
  // into each newly uploaded photo (see ImageSourceSelectorModal's handleFileInputChange).
  useEffect(() => {
    dispatch(setBatchImages(false));
    dispatch(deleteAll());
    dispatch(setID(null));
  }, [dispatch]);

  // New photos default to selected, matching the reference's grid (every thumbnail starts
  // checked). Only auto-select on growth (photos added) -- EWW-417: this used to reset to
  // "all selected" on ANY length change, so removing a selected photo (which shrinks
  // `photos.length`) would stomp the user's own selection/deselection state (e.g. a prior
  // "Deselect All") right back to all-selected. Shrinkage is already handled explicitly by
  // handleRemoveSelected's own setSelected call, so this effect should leave it alone.
  const prevPhotosLengthRef = useRef(photos.length);
  useEffect(() => {
    if (photos.length > prevPhotosLengthRef.current) {
      setSelected(new Set(photos.map((_, i) => i)));
    }
    prevPhotosLengthRef.current = photos.length;
  }, [photos.length]);

  const allSelected = selected.size > 0 && selected.size === photos.length;

  const toggleSelected = (index: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleRemoveSelected = () => {
    // Remove highest indices first so earlier indices don't shift under us.
    [...selected].sort((a, b) => b - a).forEach((index) => {
      dispatch(skipBatchUrlImage(index));
    });
    setSelected(new Set());
  };

  // EWW-459: "Apply Watermark" ignored `selected` entirely and always proceeded with the WHOLE
  // uploaded batch -- checking/unchecking thumbnails only ever fed "Remove", never this button.
  // Deselecting some photos (leaving a partial selection, e.g. 5 of 15 checked) still watermarked
  // all 15. Drops the UNSELECTED photos, the same way "Remove" already drops the selected ones,
  // so only what's actually checked carries into the editor. Matches iOS
  // (`WMBulkHomeViewController.selectedAssetsArr` is exactly what's handed to the next screen --
  // unchecked assets never ride along).
  //
  // Deliberately called from `SelectTemplate`'s `onNext`/`onCreateNew` -- i.e. only once the user
  // has actually PICKED a template and is genuinely proceeding to /edit -- and NOT from opening
  // the modal. Removing eagerly on open destroyed the unselected photos even when the user then
  // closed the modal without choosing anything, so "close without proceeding" permanently lost
  // them instead of leaving the grid untouched.
  const excludeUnselectedFromBatch = () => {
    if (selected.size === 0 || selected.size >= photos.length) return;
    const unselected = photos
      .map((_, i) => i)
      .filter((i) => !selected.has(i));
    // Remove highest indices first so earlier indices don't shift under us.
    unselected.sort((a, b) => b - a).forEach((index) => {
      dispatch(skipBatchUrlImage(index));
    });
    setSelected(new Set());
  };

  // EWW-420: dragging photos onto this page did nothing -- no onDrop/onDragOver was wired up
  // anywhere despite the empty-state copy saying "Drop your photos here". Reuses the exact
  // same upload modal/pipeline as "Choose Photos" (HEIC/TIFF/GIF handling, free-tier limit,
  // etc.) instead of duplicating it: stash the dropped files and open the modal, which feeds
  // them into its own file input (see ImageSourceSelectorModal's `initialFiles` prop).
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (isUploading || !e.dataTransfer.files.length) return;
    if (isBatchFullForFreeUser()) {
      setShowBatchLimitDialog(true);
      return;
    }
    setMultiple(true);
    setDroppedFiles(e.dataTransfer.files);
    setUploadNonce((n) => n + 1);
    setOpenUploadModal(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false);
    setDroppedFiles(null);
  };

  /** Already at the free-tier cap, so no pick could ever succeed -- surface the upgrade prompt
   *  here rather than opening the OS gallery and only rejecting it after files are chosen. The
   *  modal keeps its own equivalent check for the partially-full case (e.g. 3 uploaded, 4 more
   *  picked), which can only be judged once the selection is known. */
  const isBatchFullForFreeUser = () =>
    !isPremium && photos.length >= BATCH_LIMIT_FREE;

  const openUploader = () => {
    if (isUploading) return;
    if (isBatchFullForFreeUser()) {
      setShowBatchLimitDialog(true);
      return;
    }
    setMultiple(true);
    // Clear any previously dropped files -- otherwise a stale `droppedFiles` from an earlier
    // drag-and-drop gets fed into this fresh modal instance again instead of opening the OS
    // picker, silently "re-uploading" the same files every time this button is clicked.
    setDroppedFiles(null);
    setUploadNonce((n) => n + 1);
    setOpenUploadModal(true);
  };

  if (!isLoggedIn) return null;

  return (
    <div className="home-shell batch-watermark-shell">
      <AppSidebar
        active="batch"
        isLoggedIn={isLoggedIn}
        setOpenSignIn={setOpenSignIn}
        showPremiumUpsell
        disableNavigation={isUploading}
      />

      <div className="d-flex flex-column flex-fill" style={{ minWidth: 0 }}>
        <AppTopbar
          isLoggedIn={isLoggedIn}
          setOpenSignIn={setOpenSignIn}
          setOpenSettings={setOpenSettings}
          disableNavigation={isUploading}
        />

        <div className="batch-watermark-main">
          <section className="batch-watermark-hero">
            <div className="batch-watermark-hero__copy">
              <p className="batch-watermark-hero__welcome">
                Welcome back, {displayName}! <span aria-hidden="true">👋</span>
              </p>
              <h1 className="batch-watermark-hero__title">
                Batch Watermark
                <br />
                <span>Hundreds of Photos</span>
              </h1>
              <p className="batch-watermark-hero__subtitle">
                Upload multiple images, apply one watermark to all, and export
                everything in a single click.
              </p>
              <p className="batch-watermark-hero__formats">
                Supports JPG · PNG · WEBP · HEIC · GIF
              </p>
            </div>

            <div className="batch-watermark-hero__illustration" aria-hidden="true">
              <div className="bw-illo">
                <div className="bw-illo__card bw-illo__card--back" />
                <div className="bw-illo__card bw-illo__card--mid">
                  <div className="bw-illo__mid-block" />
                  <div className="bw-illo__mid-line" />
                  <div className="bw-illo__mid-line bw-illo__mid-line--short" />
                </div>
                <div className="bw-illo__card bw-illo__card--front">
                  <div className="bw-illo__frame">
                    <span className="bw-illo__frame-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
                        <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.6" />
                        <path d="m3 16 5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <div className="bw-illo__frame-bar" />
                  </div>
                  <div className="bw-illo__stamp">
                    <span className="bw-illo__stamp-logo">eZ</span>
                    Watermark Applied
                  </div>
                </div>
                <div className="bw-illo__badge">
                  <span className="bw-illo__badge-avatars">
                    <span className="bw-illo__badge-avatar" />
                    <span className="bw-illo__badge-avatar" />
                    <span className="bw-illo__badge-check">✓</span>
                  </span>
                  Batch Ready
                </div>
                <div className="bw-illo__pill">Processing 500+</div>
              </div>
            </div>
          </section>

          {photos.length === 0 ? (
            <section
              className={`bw-empty ${isDraggingOver ? "bw-empty--dragging" : ""}`}
              onClick={openUploader}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  openUploader();
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="bw-empty__icon">
                <svg
                  className="bw-empty__icon-sparkle bw-empty__icon-sparkle--tl"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 0l2 8 8 2-8 2-2 8-2-8-8-2 8-2z" />
                </svg>
                <svg
                  className="bw-empty__icon-glyph"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 17v-6m0 0-2.5 2.5M12 11l2.5 2.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="bw-empty__icon-plus">+</span>
                <svg
                  className="bw-empty__icon-sparkle bw-empty__icon-sparkle--br"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 0l2 8 8 2-8 2-2 8-2-8-8-2 8-2z" />
                </svg>
              </div>
              <h3 className="bw-empty__title">No photos selected yet</h3>
              <p className="bw-empty__subtitle">
                Drop your photos here, or browse from your device to start
                watermarking in bulk.
              </p>
              <div className="bw-empty__actions">
                <button
                  type="button"
                  className="bw-empty__choose"
                  onClick={(e) => {
                    e.stopPropagation();
                    openUploader();
                  }}
                >
                  Choose Photos
                </button>
              </div>
              <p className="bw-empty__hint">
                Max file size 25 MB · JPG · PNG · WEBP · HEIC · GIF
              </p>
            </section>
          ) : (
            <>
              <section
                className={`bw-grid-card ${isDraggingOver ? "bw-grid-card--dragging" : ""}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="bw-grid-card__toolbar">
                  <span className="bw-grid-card__count">
                    {/* EWW-459 follow-up: hardcoded to the total uploaded count, so this never
                        moved as photos were checked/unchecked -- looked stuck even while
                        Apply Watermark's own modal correctly showed the real selected count. */}
                    {selected.size} Photos Selected
                  </span>
                  <button
                    type="button"
                    className="bw-tool-btn"
                    onClick={() =>
                      setSelected(
                        allSelected ? new Set() : new Set(photos.map((_, i) => i))
                      )
                    }
                  >
                    {allSelected ? "Deselect All" : "Select All"}
                  </button>
                  <button
                    type="button"
                    className="bw-tool-btn"
                    disabled={selected.size === 0 || isUploading}
                    onClick={handleRemoveSelected}
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    className="bw-tool-btn"
                    disabled={isUploading}
                    onClick={openUploader}
                  >
                    + Add More Photos
                  </button>
                </div>

                <div className="bw-grid">
                  {photos.map((p, index) => (
                    <div key={index} className="bw-grid__item">
                      <button
                        type="button"
                        className="bw-grid__thumb"
                        onClick={() => toggleSelected(index)}
                      >
                        {/* EWW-449: same fix as the batch preview thumbnails -- without these,
                            the browser fetches + synchronously decodes every heavy uploaded
                            photo as soon as it mounts (not just the visible ones), which blanks
                            out and stutters scrolling on low-spec CPUs. */}
                        <img
                          src={p.url}
                          alt={p.name ?? ""}
                          loading="lazy"
                          decoding="async"
                        />
                        <span
                          className={`bw-grid__check ${
                            selected.has(index) ? "bw-grid__check--on" : ""
                          }`}
                        >
                          ✓
                        </span>
                      </button>
                      <div className="bw-grid__name">{p.name}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bw-action-bar">
                <button
                  type="button"
                  className="bw-action-bar__apply"
                  disabled={isUploading || selected.size === 0}
                  onClick={() => setOpenTemplateModal(true)}
                >
                  {isUploading ? (
                    "Uploading photos…"
                  ) : (
                    <>
                      Apply Watermark <span aria-hidden="true">&rarr;</span>
                    </>
                  )}
                </button>
              </section>
            </>
          )}
        </div>
      </div>

      {openUploadModal && (
        <ImageSourceSelectorModal
          key={uploadNonce}
          open={openUploadModal}
          onCloseAndClean={handleCloseUploadModal}
          uploadImageType={"main"}
          multiple={multiple}
          setActive={() => { return; }}
          cause={undefined}
          initialFiles={droppedFiles}
          onUploadingChange={setIsUploading}
          stayOnPage
          onStayOnPage={() => setOpenUploadModal(false)}
          autoTriggerFilePicker
        />
      )}

      <PremiumGateDialog
        open={showBatchLimitDialog}
        onClose={() => setShowBatchLimitDialog(false)}
        title="Batch Limit Reached"
        message={`Free users can process up to ${BATCH_LIMIT_FREE} images in a batch. Upgrade to Pro to process unlimited images.`}
      />

      {openTemplateModal && (
        <SelectTemplate
          open={openTemplateModal}
          setOpen={setOpenTemplateModal}
          portraitTemp={portraitTemp}
          setPortrait={setPortrait}
          landcsapeTemp={landcsapeTemp}
          setLandcsape={setLandcsape}
          setMultiple={setMultiple}
          onNext={() => {
            // Only this path actually applies a template to the uploaded batch -- `onCreateNew`
            // (below) starts a brand new template from scratch and never touches these photos.
            excludeUnselectedFromBatch();
            history.push("/edit");
          }}
          onCreateNew={() => history.push("/edit")}
          // EWW-459 follow-up: this modal's own copy ("Apply to N Photos") always read the FULL
          // batch count, even though `onNext` above now only carries the SELECTED photos
          // forward -- so it visibly promised more than it did. The Apply button is disabled
          // whenever nothing is selected, so `selected.size` is always >= 1 by the time this is
          // reachable; the fallback is just defensive.
          photoCount={selected.size || photos.length}
        />
      )}

      <SettingsModal
        open={openSettings}
        setOpen={setOpenSettings}
        handleDone={() => setOpenSettings(false)}
        parent={false}
      />
      <SignIn open={openSignIn} setOpen={setOpenSignIn} />
    </div>
  );
};

export default BatchWatermark;
